import { Injectable } from '@angular/core';
import { User } from '@app/models';
import { UpdateLabelSharedType } from '@app/pcs/actions/label';
import * as ShareFilterLabelActions from '@app/pcs/actions/share-filter-label';
import { InitShareForm, ShareFilterLabelActionTypes, ShareObjectPayload } from '@app/pcs/actions/share-filter-label';
import { UserFilter } from '@app/pcs/models';
import * as fromPcs from '@app/pcs/reducer';
import { SharedObject, SharedObjectType } from '@app/pcs/reducer/share-filter-label';
import { UserFilterDataService } from '@app/pcs/services/user-filter-data.service';
import { UserLabelDataService } from '@app/pcs/services/user-label-data.service';
import { getFilterId } from '@app/pcs/util/user-filter';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { hasValue, mapPayload } from '@util';
import * as R from 'ramda';
import { asapScheduler, Observable, of, scheduled } from 'rxjs';
import { catchError, exhaustMap, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ShareFilterLabelEffects {

  private selectedLabelIds$ = this.store.pipe(select(fromPcs.getSelectedLabelIds));
  private selectedUserFilter$ = this.store.pipe(select(fromPcs.getSelectedFilter));
  private sharedFormDisplayed$ = this.store.pipe(select(fromPcs.getDisplayShareForm));
  private sharedObject$ = this.store.pipe(select(fromPcs.getSharedObject));
  private shareFormActions = (sharedObjectType, sharedObjectId) => ([
    new ShareFilterLabelActions.SetSharedObject({
      sharedObjectType,
      sharedObjectId
    }),
    new ShareFilterLabelActions.FetchEligibleUsersForSharing({
      sharedObjectType,
      sharedObjectId
    }),
    new ShareFilterLabelActions.FetchSharedUsers({
      sharedObjectType,
      sharedObjectId
    })
  ]);

  toggleShareForm$ = createEffect(() => this.actions$.pipe(
    ofType<ShareFilterLabelActions.ToggleShareForm>(ShareFilterLabelActionTypes.ToggleShareForm),
    withLatestFrom(this.sharedFormDisplayed$, this.sharedObject$, this.selectedUserFilter$, this.selectedLabelIds$),
    switchMap(([_, sharedFormDisplayed, sharedObject, selectedFilter, selectedLabelIds]: [ShareFilterLabelActions.ToggleShareForm, boolean, SharedObject, UserFilter, number[]]) =>
      sharedFormDisplayed && !hasValue(sharedObject)
        ? this.initShareForm(selectedFilter, selectedLabelIds)
        : scheduled([], asapScheduler)
    )
  ));

  initShareForm$ = createEffect(() => this.actions$.pipe(
    ofType<ShareFilterLabelActions.InitShareForm>(ShareFilterLabelActionTypes.InitShareForm),
    mapPayload(),
    switchMap(({sharedObjectType, sharedObjectId}) => this.shareFormActions(sharedObjectType, sharedObjectId))
  ));

  fetchUsersEligibleForSharing$ = createEffect(() => this.actions$.pipe(
    ofType<ShareFilterLabelActions.FetchEligibleUsersForSharing>(ShareFilterLabelActionTypes.FetchEligibleUsersForSharing),
    mapPayload(),
    exhaustMap(({sharedObjectType, sharedObjectId}) =>
      this.getEligibleUsersForSharing({sharedObjectType, sharedObjectId}).pipe(
        switchMap((users: User[]) => of(new ShareFilterLabelActions.FetchEligibleUsersForSharingSuccess(users))),
        catchError((error: Error) => of(new ShareFilterLabelActions.FetchEligibleUsersForSharingFail(error)))
      )
    )
  ));

  fetchSharedUsers$ = createEffect(() => this.actions$.pipe(
    ofType<ShareFilterLabelActions.FetchSharedUsers>(ShareFilterLabelActionTypes.FetchSharedUsers),
    mapPayload(),
    exhaustMap(({sharedObjectType, sharedObjectId}) =>
      this.getSharedUsers({sharedObjectType, sharedObjectId}).pipe(
        switchMap((sharedUsers: User[]) => R.equals(sharedObjectType, SharedObjectType.LABEL) ? [
          new ShareFilterLabelActions.FetchSharedUsersSuccess(sharedUsers),
          new UpdateLabelSharedType({sharedUsers, labelId: sharedObjectId})
        ] : of(new ShareFilterLabelActions.FetchSharedUsersSuccess(sharedUsers))
        ),
        catchError((error: Error) => of(new ShareFilterLabelActions.FetchSharedUsersFail(error)))
      )
    )
  ));

  share$ = createEffect(() => this.actions$.pipe(
    ofType<ShareFilterLabelActions.ShareWithUsers>(ShareFilterLabelActionTypes.ShareWithUsers),
    mapPayload(),
    withLatestFrom(
      this.store.pipe(select(fromPcs.getSharedObject))
    ),
    exhaustMap(([users, {sharedObjectType, sharedObjectId}]) =>
      this.share({sharedObjectType, sharedObjectId}, users).pipe(
        switchMap(() => [
          new ShareFilterLabelActions.ShareWithUsersSuccess(),
          new ShareFilterLabelActions.FetchSharedUsers({sharedObjectType, sharedObjectId}),
          new ShareFilterLabelActions.FetchEligibleUsersForSharing({sharedObjectType, sharedObjectId})
        ]),
        catchError((error: Error) => of(new ShareFilterLabelActions.ShareWithUsersFail(error)))
      )
    )
  ));

  getEligibleUsersForSharing({sharedObjectType, sharedObjectId}: ShareObjectPayload) {
    return sharedObjectType === SharedObjectType.FILTER ? this.userFilterDataService.fetchEligibleUsersForSharing(sharedObjectId) :
      this.userLabelDataService.fetchEligibleUsersForSharing(sharedObjectId);
  }

  getSharedUsers({sharedObjectType, sharedObjectId}: ShareObjectPayload) {
    return sharedObjectType === SharedObjectType.FILTER ? this.userFilterDataService.fetchSharedUsers(sharedObjectId) :
      this.userLabelDataService.fetchSharedUsers(sharedObjectId);
  }

  share({sharedObjectType, sharedObjectId}: ShareObjectPayload, sharedUsers: number[]) {
    return sharedObjectType === SharedObjectType.FILTER ? this.userFilterDataService.shareFilter(sharedObjectId, sharedUsers) :
      this.userLabelDataService.shareLabel(sharedObjectId, sharedUsers);
  }

  initShareForm = (selectedFilter, selectedLabelIds): Observable<InitShareForm | ShareFilterLabelActions.ResetState> => R.length(selectedLabelIds) === 1
    ? of(new InitShareForm({sharedObjectType: SharedObjectType.LABEL, sharedObjectId: R.head(selectedLabelIds)} as ShareObjectPayload))
    : hasValue(selectedFilter)
      ? of(new InitShareForm({sharedObjectType: SharedObjectType.FILTER, sharedObjectId: getFilterId(selectedFilter)} as ShareObjectPayload))
      : of(new ShareFilterLabelActions.ResetState());

  constructor(private store: Store<fromPcs.State>,
    private actions$: Actions,
    private userFilterDataService: UserFilterDataService,
    private userLabelDataService: UserLabelDataService) {
  }

}
